/**
 * `Promise.withResolvers` polyfill.
 *
 * WHY: pdf.js (pdfjs-dist 5.x, behind the shared `<PdfReader>`) calls
 * `Promise.withResolvers()`. That method is only baseline from Chrome 119 /
 * Safari 17.4 / Firefox 121 — anything older throws
 * `Promise.withResolvers is not a function`, react-pdf's render blows up, and
 * the user gets the "Something went wrong" error boundary where an invoice,
 * quote or documentation PDF should be.
 *
 * That matters beyond old browsers: this SPA also ships inside Tauri webviews
 * we do NOT control the version of — WKWebView follows the host macOS (a Mac
 * on 13.x is below the bar) and Linux WebKitGTK lags badly. Cypress' bundled
 * Electron 118 is below it too, which is why the PDF-preview e2e could never
 * paint a canvas.
 *
 * Idempotent and a no-op on any engine that already has it. Must run before
 * anything imports pdf.js — call it from the app entry (`main.tsx`).
 */
export function installPromiseWithResolvers(): void {
  const P = Promise as unknown as {
    withResolvers?: <T>() => {
      promise: Promise<T>;
      resolve: (value: T | PromiseLike<T>) => void;
      reject: (reason?: unknown) => void;
    };
  };
  if (typeof P.withResolvers === "function") return;

  P.withResolvers = function withResolvers<T>() {
    let resolve!: (value: T | PromiseLike<T>) => void;
    let reject!: (reason?: unknown) => void;
    const promise = new Promise<T>((res, rej) => {
      resolve = res;
      reject = rej;
    });
    return { promise, resolve, reject };
  };
}
